import { handleError } from "../../../helperFunctions";
import { BaseAPIResponse } from "../../../utils/types";
import { baseApi } from "../../api";
import {
  GetPaymentLinkQuery,
  GetPaymentLinkResponse,
  VerifyPayment,
} from "../payment/payment.type";
import {
  AddLoanProductPayload,
  CooperativeLoansDetails,
  FailedDisbursementsQuery,
  FailedDisbursementsResponse,
  GenerateRepaymentSchedulePayload,
  GenerateRepaymentScheduleResponse,
  GetCooperativeLoanConfig,
  GetGuarantorRequestsResponse,
  MemberCancelLoanRequestPayload,
  MemberLoansDetails,
  MemberLoansDetailsQuery,
  RequestLoanPayload,
  RetryFailedDisbursementsPayload,
  SingleLoanDetailsResponse,
} from "./loans.type";

export const loansApi = baseApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    fetchGuarantorLoanRequests: builder.query<
      GetGuarantorRequestsResponse,
      { detail: boolean }
    >({
      query: (payload) => {
        return {
          url: `/Loans/detail/invitation`,
          method: "GET",
          params: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, false);
        }
      },
    }),

    toggleGuarantorApproval: builder.mutation<
      BaseAPIResponse,
      {
        coop: number;
        isaccept: boolean;
        loanid: number;
      }
    >({
      query: (payload) => {
        return {
          url: `/Loans/detail/invitation`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    getCooperativeLoanConfig: builder.query<GetCooperativeLoanConfig, void>({
      query: () => {
        return {
          url: `/Loans/settings/product`,
          method: "GET",
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, false);
        }
      },
    }),

    addLoanProduct: builder.mutation<BaseAPIResponse, AddLoanProductPayload>({
      query: (payload) => {
        return {
          url: `/Loans/settings/product`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    editLoanProduct: builder.mutation<
      BaseAPIResponse,
      { id: string; payload: AddLoanProductPayload }
    >({
      query: ({ id, payload }) => {
        return {
          url: `/Loans/settings/product/${id}`,
          method: "PUT",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    generateRepaymentSchedule: builder.query<
      GenerateRepaymentScheduleResponse,
      GenerateRepaymentSchedulePayload
    >({
      query: (payload) => {
        return {
          url: `/Loans/Generate/Repayment/Schedule`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    requestLoan: builder.mutation<BaseAPIResponse, RequestLoanPayload>({
      query: (payload) => {
        return {
          url: `/Loans/detail`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    fetchMemberLoanDetails: builder.query<
      MemberLoansDetails,
      MemberLoansDetailsQuery
    >({
      query: (payload) => {
        return {
          url: `/Loans/detail`,
          method: "GET",
          params: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, false);
        }
      },
    }),

    fetchCooperativeLoanDetails: builder.query<
      CooperativeLoansDetails,
      MemberLoansDetailsQuery
    >({
      query: (payload) => {
        return {
          url: `/Loans/admin/detail`,
          method: "GET",
          params: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, false);
        }
      },
    }),

    memberFetchLoanDetails: builder.query<
      SingleLoanDetailsResponse,
      { loan_id: number | string; detail: boolean; coop: string | number }
    >({
      query: (payload) => {
        return {
          url: `/Loans/detail/${payload.loan_id}`,
          method: "GET",
          params: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, false);
        }
      },
    }),

    officialFetchLoanDetails: builder.query<
      SingleLoanDetailsResponse,
      { loan_id: number | string; detail: boolean; coop: string | number }
    >({
      query: (payload) => {
        return {
          url: `Loans/admin/detail/${payload.loan_id}`,
          method: "GET",
          params: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, false);
        }
      },
    }),

    fetchFailedDisbursements: builder.query<
      FailedDisbursementsResponse,
      FailedDisbursementsQuery
    >({
      query: (payload) => {
        return {
          url: `Loans/retry/out`,
          method: "GET",
          params: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, false);
        }
      },
    }),

    memberUploadLoanRepayment: builder.mutation<BaseAPIResponse, FormData>({
      query: (payload) => {
        return {
          url: `/Loans/Manual/repayment`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    officialUploadLoanRepayment: builder.mutation<BaseAPIResponse, FormData>({
      query: (payload) => {
        return {
          url: `/Loans/admin/Manual/repayment`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    retryFailedDisbursements: builder.mutation<
      BaseAPIResponse,
      RetryFailedDisbursementsPayload
    >({
      query: (payload) => {
        return {
          url: `/Loans/retry/out`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    memberCancelLoanRequest: builder.mutation<
      BaseAPIResponse,
      MemberCancelLoanRequestPayload
    >({
      query: (payload) => {
        return {
          url: `/Loans/self/cancel`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    getLoanRepaymentPaystackPayment: builder.mutation<
      GetPaymentLinkResponse,
      GetPaymentLinkQuery
    >({
      query: (params) => {
        return {
          url: `/Payment/Get/link`,
          method: "GET",
          params: params,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    verifyLoanRepayment: builder.mutation<BaseAPIResponse, VerifyPayment>({
      query: (payload) => {
        return {
          url: `/Loans/Verify/repayment`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, false);
        }
      },
    }),
  }),
});

export const {
  useFetchGuarantorLoanRequestsQuery,
  useToggleGuarantorApprovalMutation,
  useGetCooperativeLoanConfigQuery,
  useAddLoanProductMutation,
  useEditLoanProductMutation,
  useGenerateRepaymentScheduleQuery,
  useRequestLoanMutation,
  useFetchMemberLoanDetailsQuery,
  useFetchCooperativeLoanDetailsQuery,
  useOfficialFetchLoanDetailsQuery,
  useMemberFetchLoanDetailsQuery,
  useFetchFailedDisbursementsQuery,
  useMemberUploadLoanRepaymentMutation,
  useOfficialUploadLoanRepaymentMutation,
  useRetryFailedDisbursementsMutation,
  useMemberCancelLoanRequestMutation,
  useGetLoanRepaymentPaystackPaymentMutation,
  useVerifyLoanRepaymentMutation,
} = loansApi;
